import { useLayoutEffect, useRef, type RefObject } from "react";

import { wrapNewWordsForStreamReveal } from "./streamWordReveal";

/** Drive the word-by-word fade-in on a streaming markdown subtree.
 *
 *  ``rootRef`` wraps the rendered markdown; each time ``source`` changes
 *  while ``isStreaming`` the newly-appended words are wrapped in
 *  ``wordClassName`` spans (see ``wrapNewWordsForStreamReveal``). When the
 *  stream ends ``settledClassName`` is added to the root — the CSS
 *  kill-switch that snaps any in-flight fades to opaque.
 *
 *  Callers should keep the same wrapper element mounted across the
 *  streaming → settled flip (rather than swapping in a plain renderer), so
 *  the settle applies in place without a remount flash. */
export const useStreamWordReveal = (
  rootRef: RefObject<HTMLElement | null>,
  source: string,
  isStreaming: boolean,
  wordClassName: string,
  settledClassName: string
): void => {
  // Number of words considered "already painted" before the next walk.
  // Anything beyond this gets the staggered fade-in; anything before is
  // left as plain text, which renders opaque with no animation. ``null``
  // means we have not walked this bubble yet.
  const settledWordCountRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    if (!isStreaming) {
      // Stream finished — snap any in-flight word fades to opaque
      // immediately. The class is the kill-switch; CSS handles the
      // rest. Reset the settled counter so a future re-stream of this
      // bubble (e.g. user hits "Try again") starts fresh.
      root.classList.add(settledClassName);
      settledWordCountRef.current = null;
      return;
    }

    root.classList.remove(settledClassName);

    // First walk since (re)entering the streaming state. Whatever is on
    // screen now arrived before we started watching, so settle it: an SSE
    // bubble mounts ~empty and this settles nothing, but a bubble filled by
    // the fallback poller — or rehydrated mid-turn after a reload — mounts
    // with a full partial answer we must NOT re-animate. ``Infinity`` marks
    // every current word settled; only text appended on later walks (the
    // next poll's new chunk) then fades in.
    const settledBefore =
      settledWordCountRef.current ?? Number.POSITIVE_INFINITY;
    const totalWords = wrapNewWordsForStreamReveal(
      root,
      settledBefore,
      wordClassName
    );
    // Words that exist as of this paint are now "settled" for the
    // purposes of the *next* render — only words appended after this
    // call will animate. This is what prevents previously-faded words
    // from re-flashing every time a new text_delta lands.
    settledWordCountRef.current = totalWords;
    // ``rootRef`` and the class names are stable; ``source`` is what
    // changes the subtree between walks.
  }, [rootRef, source, isStreaming, wordClassName, settledClassName]);
};
